/**
 * The alphabet event codes are made from. Letters and digits that look alike (I, O, 0, 1) are left out, which is also why a typed code needs no
 * guessing about them. Must equal EventCode.Alphabet in api/Domain/Identifiers.cs; a test reads that file and compares.
 */
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
export const CODE_LENGTH = 8

/**
 * A code as a person types it: any case, with spaces, dashes, dots or underscores anywhere ("abcd-efgh", " ABCD EFGH "). Returns the canonical
 * form, or null when what is left is not a code. It does not check that the event exists: the server does.
 */
export function normalizeEventCode(input: string): string | null {
  const code = input.toUpperCase().replace(/[\s\-_.]+/g, '')
  if (code.length !== CODE_LENGTH) return null
  for (const c of code) if (!CODE_ALPHABET.includes(c)) return null
  return code
}

/**
 * The event code out of the path: /e/ABC123 -> "ABC123", /wall/ABC123 -> "ABC123".
 *
 * Static Web Apps rewrites those paths to a static HTML file, so the code only
 * survives in location.pathname — there is no server-rendered variable to read
 * it from. Returns null when the path has no code, which the pages treat as
 * "this link is wrong" rather than guessing.
 */
export function codeFromPath(pathname: string, segment: 'e' | 'wall' | 'manage' | 'tv'): string | null {
  const parts = pathname.split('/').filter(Boolean)
  // The QR code on a printed card is in capitals (HTTPS://KWENTO.PHOTOS/E/{CODE}, the alphanumeric mode that keeps the code small), and a browser lowercases
  // only the scheme and the host, so the path arrives as /E/{CODE}. The segment is matched in any case.
  if (parts[0]?.toLowerCase() !== segment) return null
  const code = parts[1]
  if (!code) return null
  return decodeURIComponent(code)
}

/** The alphabet and length of a TV's pairing code. Must equal PairingCode in api/Domain/WallToken.cs; a test reads that file and compares. */
export const TV_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'
export const TV_CODE_LENGTH = 6

/** A TV's code as a person types it or a QR carries it: any case, spaces and dashes allowed. Null when what is left is not a code. */
export function normalizeTvCode(input: string): string | null {
  const code = input.toUpperCase().replace(/[\s\-_.]+/g, '')
  if (code.length !== TV_CODE_LENGTH) return null
  for (const ch of code) if (!TV_CODE_ALPHABET.includes(ch)) return null
  return code
}
